class FinalStep extends HTMLElement {
  connectedCallback() {
    this.variantTrigger = this.querySelector('.variant-trigger');
    this.variantLabel = this.querySelector('.variant-label');
    this.cancelInfo = this.querySelector('.cancel-info');
    this.ctaBtn = this.querySelector('.btn--add-to-cart');
    this.basePriceEl = this.querySelector('.base__price');
    this.basePriceValue = this.querySelector('.base__price-value');

    // Popup lives at mm-footer level (sibling to final-step) so it can overlay both columns
    const footer = this.closest('.mm-footer');
    this.variantPopup = footer?.querySelector('.variant-picker-popup') ?? null;
    this.popupCtaBtn = this.variantPopup?.querySelector('.btn--add-to-cart-popup') ?? null;
    this.priceCompareEls = [
      this.querySelector('.cta .price-display__compare'),
      this.variantPopup?.querySelector('.price-display__compare'),
    ].filter(Boolean);
    this.priceCurrentEls = [
      this.querySelector('.cta .price-display__current'),
      this.variantPopup?.querySelector('.price-display__current'),
    ].filter(Boolean);
    this.ctaBtns = [this.ctaBtn, this.popupCtaBtn].filter(Boolean);
    this.popupClose = this.variantPopup?.querySelector('.popup__close') ?? null;
    this.popupOverlay = this.variantPopup?.querySelector('.variant-picker-popup__overlay') ?? null;
    this.variantOptions = [...(this.variantPopup?.querySelectorAll('.variant-option') ?? [])];
    this._isPageScrollLocked = false;
    this._scrollYBeforeLock = 0;
    this._bodyScrollLockStyles = null;

    this.variantTrigger?.addEventListener('click', () => this._togglePopup());
    this.variantTrigger?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this._togglePopup(); }
    });
    this.popupClose?.addEventListener('click', () => this._closePopup());
    this.popupOverlay?.addEventListener('click', () => this._closePopup());
    this.ctaBtns.forEach((btn) => {
      btn.addEventListener('click', () => this._addToCart());
    });

    this.variantOptions.forEach(opt => {
      opt.addEventListener('click', () => this._selectOption(opt));
    });

    subscribe(PUB_SUB_EVENTS.quantityUpdate, (state) => this._onUpdate(state));
    subscribe(PUB_SUB_EVENTS.variantChange, (state) => this._onUpdate(state));

    // Set initial active option and label
    this._syncActiveOption();
    this._onUpdate(window.mmStorage.snapshot());
  }

  disconnectedCallback() {
    this._unlockPageScroll();
  }

  _onUpdate(state) {
    this._updatePrice(state);
    this._updateBasePrice(state);
    this._updateButton(state);
  }

  _updatePrice(state) {
    if (state.totalQuantity === 0) {
      this.priceCurrentEls.forEach((el) => { el.textContent = ''; });
      this.priceCompareEls.forEach((el) => {
        el.textContent = '';
        el.classList.add('hidden');
      });
      return;
    }

    const currentPrice = _formatPrice(state.totalPrice);
    this.priceCurrentEls.forEach((el) => { el.textContent = currentPrice; });

    if (state.totalDiscount > 0) {
      const comparePrice = _formatPrice(state.totalCompareAtPrice);
      this.priceCompareEls.forEach((el) => {
        el.textContent = comparePrice;
        el.classList.remove('hidden');
      });
    } else {
      this.priceCompareEls.forEach((el) => {
        el.textContent = '';
        el.classList.add('hidden');
      });
    }
  }

  _updateBasePrice(state) {
    if (!this.basePriceEl || !this.basePriceValue) return;

    const avg = state.averageBasePricePerKg;
    if (!state.totalQuantity || !avg || avg <= 0) {
      this.basePriceValue.textContent = '0';
      this.basePriceEl.classList.add('hidden');
      return;
    }

    this.basePriceValue.textContent = String(avg);
    this.basePriceEl.classList.remove('hidden');
  }

  _updateButton(state) {
    this.ctaBtns.forEach((btn) => {
      btn.disabled = !state.canCheckout;
    });
  }

  _togglePopup() {
    const isHidden = this.variantPopup.classList.contains('hidden');
    if (isHidden) {
      this._openPopup();
    } else {
      this._closePopup();
    }
  }

  _openPopup() {
    if (!this.variantPopup) return;
    this.variantPopup.classList.remove('hidden');
    this._lockPageScroll();
    if (typeof window.toggleChatIcon === 'function') {
      window.toggleChatIcon({ display: false });
    }
  }

  _closePopup() {
    if (!this.variantPopup) return;
    this.variantPopup.classList.add('hidden');
    this._unlockPageScroll();
    if (typeof window.toggleChatIcon === 'function' && !window.isOverlayBlockingChat()) {
      window.toggleChatIcon({ display: true });
    }
  }

  _lockPageScroll() {
    if (this._isPageScrollLocked) return;

    this._scrollYBeforeLock = window.scrollY || document.documentElement.scrollTop || 0;
    this._bodyScrollLockStyles = {
      position: document.body.style.position,
      top: document.body.style.top,
      width: document.body.style.width,
      overflow: document.body.style.overflow,
    };

    if (typeof lockBodyScroll === 'function') lockBodyScroll();

    document.body.style.position = 'fixed';
    document.body.style.top = `-${this._scrollYBeforeLock}px`;
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
    this._isPageScrollLocked = true;
  }

  _unlockPageScroll() {
    if (!this._isPageScrollLocked) return;

    if (typeof unlockBodyScroll === 'function') unlockBodyScroll();

    const styles = this._bodyScrollLockStyles || {};
    document.body.style.position = styles.position || '';
    document.body.style.top = styles.top || '';
    document.body.style.width = styles.width || '';
    document.body.style.overflow = styles.overflow || '';

    window.scrollTo(0, this._scrollYBeforeLock);
    this._isPageScrollLocked = false;
    this._bodyScrollLockStyles = null;
  }

  _selectOption(opt) {
    const days = parseInt(opt.dataset.days) || 30;
    const type = opt.dataset.type || 'subscription';
    window.mmStorage.setVariant(days, type);
    this._syncActiveOption();

    const keepPopupOpen = window.matchMedia('(max-width: 749px)').matches;
    if (!keepPopupOpen) {
      this._closePopup();
    }
  }

  _syncActiveOption() {
    const storage = window.mmStorage;
    let activeLabel = null;
    let shouldHideCancelInfo = false;

    this.variantOptions.forEach(opt => {
      const days = parseInt(opt.dataset.days) || 30;
      const type = opt.dataset.type || 'subscription';
      const isActive = type === storage.purchaseType &&
        (type === 'onetimer' || days === storage.variantDays);

      opt.classList.toggle('active', isActive);
      opt.setAttribute('aria-checked', String(isActive));

      if (isActive) activeLabel = opt.querySelector('.variant-option__title-js')?.textContent ?? null;
      if (isActive) shouldHideCancelInfo = opt.dataset.hideCancelInfo === 'true';
    });

    if (activeLabel && this.variantLabel) {
      this.variantLabel.textContent = activeLabel;
    }

    if (this.cancelInfo) {
      this.cancelInfo.classList.toggle('hidden', shouldHideCancelInfo);
    }
  }

  async _addToCart() {
    const storage = window.mmStorage;
    const { paidItems, giftItems } = storage.buildMixMatchCheckoutPlan();
    if (!paidItems.length) return;

    this.ctaBtns.forEach((btn) => { btn.disabled = true; });

    try {
      await AjaxCart.addToCart(JSON.stringify({ items: paidItems }), 'mix-match');

      if (giftItems.length) {
        await AjaxCart.addToCart(JSON.stringify({ items: giftItems }), 'mix-match');
      }

      if (typeof CartItems !== 'undefined' && CartItems.cartDrawerRenderContent) {
        CartItems.cartDrawerRenderContent();
      }

      this._closePopup();
    } catch (e) {
      console.error(e);
    } finally {
      this.ctaBtns.forEach((btn) => { btn.disabled = !storage.canCheckout; });
    }
  }
}

if (!customElements.get('final-step')) {
  customElements.define('final-step', FinalStep);
}
